import React, { useState } from 'react';
import { Send, CheckCircle2, Megaphone, Target, DollarSign, Calendar, AlertCircle } from 'lucide-react';

export function MetaAdsPublisherModal({
  isOpen,
  onClose,
  activeClient,
  videoData
}) {
  const [campaignObjective, setCampaignObjective] = useState('OUTCOME_LEADS'); // Leads / Messages
  const [dailyBudget, setDailyBudget] = useState('150'); // $150 MXN
  const [adTitle, setAdTitle] = useState(() => videoData?.title || `Campaña ${activeClient?.name || 'Cliente'}`);
  const [adCopy, setAdCopy] = useState(() => videoData?.script || 'Descubre nuestra solución y agenda hoy por WhatsApp.');
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishedResult, setPublishedResult] = useState(null);

  if (!isOpen) return null;

  const handlePublish = async (e) => {
    e.preventDefault();
    setIsPublishing(true);
    try {
      const res = await fetch('/api/meta/campaigns/publish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: activeClient?.id,
          clientName: activeClient?.name,
          campaignObjective,
          dailyBudget,
          adTitle,
          adCopy,
          videoUrl: videoData?.videoUrl || '/avatar_upscaled.jpg',
          ratio: videoData?.ratio || '9:16'
        })
      });
      const data = await res.json();
      if (data.success) {
        setPublishedResult(data.campaign);
      }
    } catch (err) {
      console.error('Error publicando en Meta:', err);
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(5, 7, 12, 0.85)',
      backdropFilter: 'blur(16px)',
      zIndex: 1250,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: '#111420',
        borderRadius: '16px',
        border: '1px solid rgba(59, 130, 246, 0.4)',
        boxShadow: '0 25px 60px -15px rgba(0,0,0,0.9), 0 0 40px rgba(59, 130, 246, 0.2)',
        width: '100%',
        maxWidth: '620px',
        maxHeight: '90vh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '24px'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ background: 'rgba(59, 130, 246, 0.15)', padding: '8px', borderRadius: '10px', color: '#60a5fa' }}>
              <Megaphone size={22} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2 style={{ fontSize: '17px', fontWeight: 800, color: '#f8fafc', margin: 0 }}>
                  Lanzador Automático de Campañas Meta Ads
                </h2>
              </div>
              <p style={{ fontSize: '11px', color: '#94a3b8', margin: '2px 0 0 0' }}>
                Empresa: <strong style={{ color: '#60a5fa' }}>{activeClient?.name || 'Cliente'}</strong> • Facebook e Instagram Ads API
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#64748b', fontSize: '20px', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>

        {publishedResult ? (
          <div style={{
            background: 'rgba(16, 185, 129, 0.08)',
            border: '1px solid #10b981',
            borderRadius: '12px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: '12px'
          }}>
            <div style={{ background: '#10b981', color: '#fff', padding: '10px', borderRadius: '50%' }}>
              <CheckCircle2 size={32} />
            </div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#10b981' }}>
              ¡Campaña Publicada en Meta Ads!
            </div>
            <div style={{ fontSize: '12px', color: '#cbd5e1', lineHeight: '1.5', maxWidth: '420px' }}>
              El video de <strong>{activeClient?.name}</strong> fue cargado en la cuenta de anuncios (ID: <code>{publishedResult.adAccountId}</code>). La campaña está en revisión y comenzará a generar clientes por WhatsApp en breve.
            </div>
            <div style={{
              background: '#090a0f',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '11px',
              color: '#94a3b8',
              fontFamily: 'monospace'
            }}>
              Campaign ID: {publishedResult.campaignId} • Status: ACTIVE (Learning)
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{
                marginTop: '6px',
                padding: '10px 20px',
                borderRadius: '8px',
                background: '#10b981',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Entendido y Cerrar
            </button>
          </div>
        ) : (
          <form onSubmit={handlePublish} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {/* Objetivo y Presupuesto */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                  Objetivo de la Campaña
                </label>
                <select
                  value={campaignObjective}
                  onChange={(e) => setCampaignObjective(e.target.value)}
                  style={{
                    width: '100%',
                    background: '#161926',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    padding: '8px 10px',
                    color: '#fff',
                    fontSize: '12px',
                    outline: 'none'
                  }}
                >
                  <option value="OUTCOME_LEADS">Mensajes directos a WhatsApp (Leads)</option>
                  <option value="OUTCOME_TRAFFIC">Visitas a Página Web Médica/B2B</option>
                  <option value="OUTCOME_AWARENESS">Alcance Masivo y Reconocimiento de Marca</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                  Presupuesto Diario ($ MXN)
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    value={dailyBudget}
                    onChange={(e) => setDailyBudget(e.target.value)}
                    style={{
                      width: '100%',
                      background: '#161926',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      padding: '8px 10px 8px 24px',
                      color: '#fff',
                      fontSize: '12px',
                      outline: 'none'
                    }}
                  />
                  <span style={{ position: 'absolute', left: '10px', top: '8px', color: '#94a3b8', fontSize: '12px' }}>$</span>
                </div>
              </div>
            </div>

            {/* Título del Anuncio */}
            <div>
              <label style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                Título del Anuncio (Headline)
              </label>
              <input
                type="text"
                value={adTitle}
                onChange={(e) => setAdTitle(e.target.value)}
                style={{
                  width: '100%',
                  background: '#161926',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  padding: '8px 10px',
                  color: '#fff',
                  fontSize: '12px',
                  outline: 'none'
                }}
                required
              />
            </div>

            {/* Texto Principal / Copy */}
            <div>
              <label style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                Texto Principal del Anuncio (Copy en Facebook/Instagram)
              </label>
              <textarea
                value={adCopy}
                onChange={(e) => setAdCopy(e.target.value)}
                rows={3}
                style={{
                  width: '100%',
                  background: '#161926',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  padding: '8px 10px',
                  color: '#fff',
                  fontSize: '12px',
                  outline: 'none',
                  resize: 'none'
                }}
              />
            </div>

            {/* Previsualización del Formato */}
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '8px',
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '11px',
              color: '#94a3b8'
            }}>
              <span>Ubicación: <strong>Reels, Stories y Feeds (Meta Network)</strong></span>
              <span style={{ color: '#60a5fa', fontWeight: 700 }}>Formato: {videoData?.ratio || '9:16'} • {videoData?.duration || '15s'}</span>
            </div>

            {/* Botón Publicar */}
            <button
              type="submit"
              disabled={isPublishing}
              style={{
                marginTop: '6px',
                padding: '13px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 800,
                border: 'none',
                cursor: isPublishing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 20px rgba(37, 99, 235, 0.4)'
              }}
            >
              <Send size={15} />
              {isPublishing ? 'Subiendo video y creando campaña en Meta...' : `🚀 Lanzar Campaña para ${activeClient?.name || 'Cliente'}`}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
